import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Users, Clock, CalendarRange, Package } from "lucide-react";
import { Input } from "@/components/ui/input";
import { durationDays } from "../lib/duration";

/**
 * Labor & Scopes panel — per-project crew size, per-scope budgeted hours and
 * the durations derived from the shared formula (see lib/duration.js).
 */
export default function ProjectLaborPanel({ project, onUpdated }) {
  const [data, setData] = useState({ scopes: [], settings: { hours_per_day: 8, duration_budget_factor: 0.8 }, default_crew_size: 2 });
  const [crew, setCrew] = useState(project.default_crew_size ?? 2);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/projects/${project.id}/scopes`);
      setData(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.id]);
  useEffect(() => { setCrew(project.default_crew_size ?? 2); }, [project.default_crew_size]);

  const hpd = data.settings?.hours_per_day ?? 8;
  const factor = data.settings?.duration_budget_factor ?? 0.8;

  const saveProjectField = async (field, value) => {
    try {
      const r = await api.patch(`/projects/${project.id}`, { [field]: value });
      onUpdated?.(r.data);
      toast.success("Saved");
      load();
    } catch { toast.error("Save failed"); }
  };

  const saveScope = async (scopeType, patch) => {
    try {
      await api.patch(`/projects/${project.id}/scopes/${encodeURIComponent(scopeType)}`, patch);
      load();
    } catch { toast.error("Scope update failed"); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="project-labor-panel">
      <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center gap-2 flex-wrap">
        <Users className="w-4 h-4 text-gray-500" />
        <span className="text-sm font-semibold text-gray-700">Labor &amp; Scopes</span>
        <span className="text-xs text-gray-400 ml-1">
          <Clock className="w-3 h-3 inline mr-1" />
          {hpd} hrs/day · factor {factor} (Settings → Labor defaults)
        </span>
      </div>

      <div className="px-4 py-3 flex flex-wrap items-end gap-6 border-b border-gray-100">
        <label className="text-xs text-gray-500">
          <span className="block mb-1">Default crew size</span>
          <Input
            data-testid="project-default-crew-size"
            type="number"
            min={1}
            value={crew}
            onChange={(e) => setCrew(e.target.value)}
            onBlur={() => Number(crew) !== (project.default_crew_size ?? 2) && saveProjectField("default_crew_size", Math.max(1, parseInt(crew, 10) || 1))}
            className="h-8 w-20"
          />
        </label>
        <div className="text-xs text-gray-500">
          <span className="block mb-1"><Package className="w-3 h-3 inline mr-1" />Material · Doc Control</span>
          <span className="text-[11px] text-gray-500" data-testid="project-material-doc-note">
            Read on the RA list — derived from POs, the on-hand ledger and submittals, not typed here.
          </span>
        </div>
        <div className="text-xs text-gray-500">
          <span className="block mb-1"><CalendarRange className="w-3 h-3 inline mr-1" />Dates</span>
          <span className="text-sm text-gray-700" data-testid="project-dates-summary">
            {(project.start_date || "—")} → {(project.abs_completion_date || "—")}
            {project.gc_completion_date ? <span className="text-gray-400"> · GC {project.gc_completion_date}</span> : null}
          </span>
        </div>
      </div>

      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr className="text-[10px] text-gray-500 uppercase tracking-wider">
            <th className="text-left px-4 py-2 font-medium">Scope</th>
            <th className="text-left px-3 py-2 font-medium">Budgeted hours <span className="normal-case text-gray-400">(manual — Procore sync will overwrite)</span></th>
            <th className="text-left px-3 py-2 font-medium">Crew size</th>
            <th className="text-left px-3 py-2 font-medium">% Complete</th>
            <th className="text-left px-3 py-2 font-medium">Consumed</th>
            <th className="text-left px-3 py-2 font-medium">Hrs remaining</th>
            <th className="text-left px-3 py-2 font-medium">Duration</th>
            <th className="text-left px-3 py-2 font-medium">Remaining</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-500">Loading…</td></tr>
          ) : (data.scopes || []).length === 0 ? (
            <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-500">No scopes yet — upload an FRS to populate them.</td></tr>
          ) : data.scopes.map((s) => (
            <ScopeRow
              key={s.scope_type}
              scope={s}
              defaultCrew={project.default_crew_size ?? data.default_crew_size ?? 2}
              hpd={hpd}
              factor={factor}
              onSave={saveScope}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ScopeRow({ scope, defaultCrew, hpd, factor, onSave }) {
  const [hours, setHours] = useState(scope.budgeted_hours || "");
  const [crew, setCrew] = useState(scope.crew_size ?? "");
  useEffect(() => { setHours(scope.budgeted_hours || ""); setCrew(scope.crew_size ?? ""); }, [scope.budgeted_hours, scope.crew_size]);

  const effCrew = Number(crew) || defaultCrew;
  const days = durationDays(hours, effCrew, hpd, factor);
  const remaining = durationDays(hours, effCrew, hpd, factor, scope.percent_complete || 0);
  // Cluster 8B — field progress drives consumed / remaining hours.
  const pct = Math.round((scope.percent_complete || 0) * 100);
  const consumed = Math.round(Number(hours || 0) * (scope.percent_complete || 0) * 10) / 10;
  const remainingHours = Math.round((Number(hours || 0) - consumed) * 10) / 10;
  const st = scope.scope_type;

  return (
    <tr className="border-t border-gray-100" data-testid={`scope-row-${st}`}>
      <td className="px-4 py-2 font-medium text-gray-800">
        {scope.scope_code || st}
        {scope.scope_code && scope.scope_code !== st && <span className="text-xs text-gray-400 ml-1.5">{st}</span>}
        {scope.hours_source === "procore" && <span className="ml-2 text-[10px] uppercase text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1">Procore</span>}
      </td>
      <td className="px-3 py-2">
        <Input
          data-testid={`scope-hours-${st}`}
          type="number"
          min={0}
          value={hours}
          onChange={(e) => setHours(e.target.value)}
          onBlur={() => Number(hours || 0) !== Number(scope.budgeted_hours || 0) && onSave(st, { budgeted_hours: Number(hours) || 0, hours_source: "manual" })}
          className="h-8 w-28"
          placeholder="0"
        />
      </td>
      <td className="px-3 py-2">
        <Input
          data-testid={`scope-crew-${st}`}
          type="number"
          min={0}
          value={crew}
          onChange={(e) => setCrew(e.target.value)}
          onBlur={() => (Number(crew) || 0) !== (scope.crew_size || 0) && onSave(st, { crew_size: Number(crew) || 0 })}
          className="h-8 w-20"
        />
        {!crew && <span className="text-[10px] text-gray-400 ml-1.5">inherits {defaultCrew}</span>}
      </td>
      <td className="px-3 py-2 text-gray-800 tabular-nums" data-testid={`scope-pct-${st}`}>
        {pct > 0 ? <span className={pct >= 100 ? "text-emerald-700 font-semibold" : ""}>{pct}%</span>
          : <span className="text-gray-400">0%</span>}
      </td>
      <td className="px-3 py-2 text-gray-800 tabular-nums" data-testid={`scope-consumed-${st}`}>
        {Number(hours || 0) > 0 ? `${consumed} h` : "—"}
      </td>
      <td className="px-3 py-2 text-gray-800 tabular-nums" data-testid={`scope-hours-remaining-${st}`}>
        {Number(hours || 0) > 0 ? `${remainingHours} h` : "—"}
      </td>
      <td className="px-3 py-2 text-gray-800" data-testid={`scope-duration-${st}`}>
        {days > 0 ? `${days} day${days === 1 ? "" : "s"}` : "—"}
      </td>
      <td className="px-3 py-2 text-gray-500" data-testid={`scope-remaining-${st}`}>
        {remaining > 0 ? `${remaining} day${remaining === 1 ? "" : "s"}` : "—"}
        {scope.percent_complete > 0 && <span className="text-[10px] text-gray-400 ml-1">({Math.round(scope.percent_complete * 100)}% done)</span>}
      </td>
    </tr>
  );
}
